/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint max-len: 0 */
import React, {
  useEffect, useState, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import translationKeys from 'translations/translationKeys';
import { checkUnprintedTicket, printTicket } from 'redux/print/printTicketSlice';
import {
  updateConfig,
  fetchPrinters,
  checkPrinterStatus,
  clearSaveSuccess,
  clearStatusError,
  selectPrinterConfig,
  selectPrinters,
  selectPrintersLoading,
  selectPrintersError,
  selectStatusChecking,
  selectLastCheckedPrinter,
} from '../../../../redux/print-config/printConfigSlice';
import printerwhite from '../../../../assets/images/mathys/svg/printer-logo.svg';
import '../../../../styles/_printer-config.scss';

const {
  msg49, msg51, msg53,
  msg216, msg239, msg241, msg243, msg244,
  msg245, msg246, msg247,
  msg251, msg252, msg253, msg254, msg255, msg256,
  msg257, msg258, msg259, msg260, msg261, msg262,
  msg263, msg264, msg265, msg268,
  msg269, msg270, msg271, msg276,
} = translationKeys;

// ── Status helpers ────────────────────────────────────────────────────────────

function getStatusClass(printer) {
  if (!printer) return 'unknown';
  if (printer.isReady) return 'ready';
  if (printer.severity === 'WARN') return 'warn';
  if (printer.severity === 'FATAL' || printer.severity === 'ERROR') return 'error';
  return 'unknown';
}

// Returns the translationKey for a printer's status so the label is always translated.
function getStatusKey(printer) {
  if (!printer) return msg262;
  if (printer.isReady) return msg253;
  switch (printer.statusText) {
    case 'OFFLINE': return msg254;
    case 'PAPER_OUT':
    case 'PAPEROUT': return msg255;
    case 'PAPER_JAM': return msg256;
    case 'DOOR_OPEN': return msg257;
    case 'PAUSED': return msg258;
    case 'USER_INTERVENTION': return msg259;
    case 'OUTPUT_BIN_FULL': return msg260;
    case 'TONER_LOW': return msg261;
    default: return msg262;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

const PrinterConfig = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const savedConfig = useSelector(selectPrinterConfig);
  const printers = useSelector(selectPrinters);
  const printersLoading = useSelector(selectPrintersLoading);
  const printersError = useSelector(selectPrintersError);
  const statusChecking = useSelector(selectStatusChecking);
  const lastChecked = useSelector(selectLastCheckedPrinter);
  const { unPrintedTicket, printError, isPrintLoading } = useSelector((store) => store.printTicket);

  const [draft, setDraft] = useState({ ...savedConfig });

  useEffect(() => {
    setDraft({ ...savedConfig });
  }, [savedConfig]);

  useEffect(() => {
    let timeoutId;

    if (savedConfig.qzPrintingEnabled && (printers.length < 1)) {
      timeoutId = setTimeout(() => {
        dispatch(fetchPrinters());
      }, 250);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [savedConfig.qzPrintingEnabled, dispatch]);

  useEffect(() => {
    dispatch(checkUnprintedTicket());
  }, [dispatch]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleField = useCallback((field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handlePrinterSelect = useCallback((name) => {
    setDraft((prev) => ({ ...prev, printerName: name }));
  }, [printers]);

  const handleToggleQz = useCallback(() => {
    const next = !draft.qzPrintingEnabled;
    setDraft((prev) => ({ ...prev, qzPrintingEnabled: next }));
    if (next) {
      dispatch(fetchPrinters());
    } else {
      dispatch(clearStatusError());
    }
  }, [draft.qzPrintingEnabled, dispatch]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    dispatch(updateConfig({
      printerName: draft.printerName,
      cpl: parseInt(draft.cpl, 10),
      count: parseInt(draft.count, 10),
      qzPrintingEnabled: draft.qzPrintingEnabled,
    }));
  }, [draft, dispatch]);

  const handleReset = useCallback(() => {
    setDraft({ ...savedConfig });
    dispatch(clearSaveSuccess());
    dispatch(clearStatusError());
  }, [savedConfig, dispatch]);

  const handleRefreshPrinters = useCallback(() => {
    dispatch(clearStatusError());
    dispatch(fetchPrinters());
  }, [dispatch]);

  const handleCheckStatus = useCallback(() => {
    if (!draft.printerName) return;
    dispatch(clearStatusError());
    dispatch(checkPrinterStatus(draft.printerName));
  }, [draft.printerName, dispatch]);

  // ── Derived ────────────────────────────────────────────────────────────────

  const isDirty = JSON.stringify(draft) !== JSON.stringify(savedConfig);

  const selectedPrinterInfo = printers.find((p) => p.name === draft.printerName)
    || (lastChecked?.name === draft.printerName ? lastChecked : null);

  const cplOptions = [
    { value: 32, label: t(msg269) },
    { value: 40, label: t(msg270) },
    { value: 48, label: t(msg271) },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="details printer-config">

      <div className="header">
        <img src={printerwhite} alt="" />
        <span>{t(msg239)}</span>
      </div>

      <div className="content steps">
        <form onSubmit={handleSubmit}>
          <div className="content-grid">
            <div className="row">
              {/* ═══ 1 — Mode d'impression ═══════════════════════════════════ */}
              <div className="printer-config__section">
                <p className="printer-config__section-title">{t(msg241)}</p>
                <div className="config-row">
                  <div className="config-column">
                    <div className="printer-config__toggle-info">
                      <span className="printer-config__toggle-label">{t(draft.qzPrintingEnabled ? msg245 : msg276)}</span>
                      <span className="printer-config__toggle-hint">
                        {t(draft.qzPrintingEnabled ? msg246 : msg247)}
                      </span>
                    </div>
                    <div className="form-check form-switch printer-config__switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="qzToggle"
                        checked={draft.qzPrintingEnabled}
                        onChange={handleToggleQz}
                      />
                      <label className="form-check-label" htmlFor="qzToggle" />
                    </div>
                  </div>
                  {unPrintedTicket && !printError && !isPrintLoading && (
                  <div className="config-column">
                    <span className="printer-config__warn-label">{t(msg216)}</span>
                    <button
                      type="button"
                      className="btn btn-ticket"
                      onClick={() => {
                        const stored = localStorage.getItem('currentTicket');
                        if (!stored) return;
                        const { data } = JSON.parse(stored);
                        dispatch(printTicket(data));
                      }}
                    >
                      {t(msg53)}
                    </button>
                  </div>
                  )}
                </div>
              </div>
            </div>
            {draft.qzPrintingEnabled && (
            <>
              {/* ═══ 2b — Printer list (QZ mode) ═════════════════════════════ */}
              <div className="row">
                <div className="printer-config__section">
                  <div className="printer-config__section-header">
                    <p className="printer-config__section-title">{t(msg243)}</p>
                    <div className="printer-config__section-actions">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleRefreshPrinters}
                        disabled={printersLoading}
                      >
                        {printersLoading && (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        )}
                        {t(msg251)}
                      </button>
                      {selectedPrinterInfo && !printersLoading && (printers.length > 0) && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCheckStatus}
                        disabled={statusChecking || !draft.printerName}
                      >
                        {statusChecking && (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        )}
                        {t(msg252)}
                      </button>
                      )}
                    </div>
                  </div>

                  {selectedPrinterInfo && !printersLoading && (printers.length > 0) && (
                  <div className={`printer-config__status-badge printer-config__status-badge--${getStatusClass(selectedPrinterInfo)}`}>
                    <span className="printer-config__status-dot" />
                    <span>
                      {draft.printerName}
                      {' \u2014 '}
                      {t(getStatusKey(selectedPrinterInfo))}
                    </span>
                  </div>
                  )}

                  {printersLoading && (
                  <div className="loader-container">
                    <div className="spinner-border large" role="status">
                      <span className="visually-hidden">{t(msg263)}</span>
                    </div>
                    <span>{t(msg263)}</span>
                  </div>
                  )}

                  {!printersLoading && !printers.length && !printersError && (
                  <p className="printer-config__empty-hint">{t(msg264)}</p>
                  )}

                  {!printersLoading && (printers.length > 0) && (
                  <div className="printer-config__printer-list">
                    {printers.map((printer) => {
                      const statusClass = getStatusClass(printer);
                      const isSelected = draft.printerName === printer.name;

                      return (
                        <div
                          key={printer.name}
                          className={[
                            'printer-config__printer-row',
                            isSelected ? 'printer-config__printer-row--selected' : '',
                          ].join(' ')}
                        >
                          <div className="radio-option">
                            <input
                              type="radio"
                              id={`printer-${printer.name}`}
                              name="printerName"
                              value={printer.name}
                              checked={isSelected}
                              onChange={() => handlePrinterSelect(printer.name)}
                            />
                            <label htmlFor={`printer-${printer.name}`}>
                              <span className="printer-config__printer-name">{printer.name}</span>
                              <span className="printer-config__printer-meta">
                                {printer.isDefault && (
                                <span className="printer-config__default-badge">{t(msg265)}</span>
                                )}
                                <span className={`printer-config__status-inline printer-config__status-inline--${statusClass}`}>
                                  <span className="printer-config__status-dot" />
                                  {t(getStatusKey(printer))}
                                </span>
                              </span>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="printer-config__section">
                  <p className="span printer-config__section-title">{t(msg244)}</p>
                  <fieldset className="input-wrap">
                    <label className="label" htmlFor="printer-cpl">{t(msg268)}</label>
                    <input
                      type="text"
                      className="form-control"
                      id="printer-cpl"
                      placeholder=""
                      required
                      onKeyDown={(e) => !(/^\d+$/.test(e.key) || e.key === 'Backspace' || e.key === 'Delete') && e.preventDefault()}
                      value={draft.cpl}
                      onChange={(e) => {
                        handleField('cpl', e.target.value);
                      }}
                    />
                    <div className="block printer-config__option-block">
                      {cplOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`btn btn-secondary${draft.cpl === opt.value ? ' active' : ''}`}
                          onClick={() => handleField('cpl', opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                </div>
              </div>
            </>
            )}
            {/* ═══ Actions ════════════════════════════════════════════════ */}
            <div className="row">
              <div className="printer-config__actions">
                <button
                  type="button"
                  className="btn btn-secondary printer-config__btn-cancel"
                  onClick={handleReset}
                  disabled={!isDirty}
                >
                  {t(msg51)}
                </button>
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={!isDirty}
                >
                  {t(msg49)}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PrinterConfig;
